import { STORAGE_KEYS } from "@fluid-app/storage-manager";

import { generateFingerprint } from "../utils/environment";

// Add a new storage key for tracking synced fingerprints
const FINGERPRINT_SYNCED_KEY = `${STORAGE_KEYS.FINGERPRINT}_SYNCED`;

/**
 * Manages browser fingerprinting operations
 */
export class FingerPrintManager {
  private storageManager: {
    getItem: (key: string) => string | null;
    setItem: (key: string, value: string) => void;
  };
  private currentFingerprint: string | null = null;
  private fingerprintSynced = false;

  /**
   * Create a new FingerPrintManager
   * @param storageManager Storage manager instance
   */
  constructor(storageManager: {
    getItem: (key: string) => string | null;
    setItem: (key: string, value: string) => void;
  }) {
    this.storageManager = storageManager;

    // Check if the current fingerprint has been synced already
    this.loadSyncStatus();
  }

  /**
   * Load the sync status from storage
   */
  private loadSyncStatus(): void {
    const storedFingerprint = this.getStoredFingerprint();
    const syncedFingerprint = this.storageManager.getItem(
      FINGERPRINT_SYNCED_KEY,
    );

    // If we have both a stored fingerprint and a synced fingerprint, and they match,
    // then this fingerprint has already been synced
    this.fingerprintSynced = !!(
      storedFingerprint &&
      syncedFingerprint &&
      storedFingerprint === syncedFingerprint
    );

    if (this.fingerprintSynced) {
      console.debug(
        "[FairShare] Loaded existing fingerprint that has already been synced with server",
      );
    }
  }

  /**
   * Generate a browser fingerprint
   * @returns A unique fingerprint string
   */
  public async generateFingerprint(): Promise<string> {
    return generateFingerprint();
  }

  /**
   * Store a fingerprint in local storage
   * @param fingerprint The fingerprint to store
   */
  public storeFingerprint(fingerprint: string): void {
    const existingFingerprint = this.getStoredFingerprint();
    const isNewFingerprint = fingerprint !== existingFingerprint;

    this.currentFingerprint = fingerprint;
    this.storageManager.setItem(STORAGE_KEYS.FINGERPRINT, fingerprint);

    // If this is a new fingerprint, mark it as not synced
    if (isNewFingerprint) {
      this.fingerprintSynced = false;
      // Clear the synced status in storage
      this.storageManager.setItem(FINGERPRINT_SYNCED_KEY, "");
    }
  }

  /**
   * Get the stored fingerprint from local storage
   * @returns The stored fingerprint or null if not found
   */
  public getStoredFingerprint(): string | null {
    return this.storageManager.getItem(STORAGE_KEYS.FINGERPRINT);
  }

  /**
   * Set the current fingerprint in memory
   * @param fingerprint The fingerprint to set
   */
  public setFingerprint(fingerprint: string): void {
    // Don't mark as not synced just because we're setting the fingerprint in memory
    // Instead, check if it's the same as what's in storage and what's been synced
    const storedFingerprint = this.getStoredFingerprint();
    const syncedFingerprint = this.storageManager.getItem(
      FINGERPRINT_SYNCED_KEY,
    );

    this.currentFingerprint = fingerprint;

    // Only mark as not synced if:
    // 1. The fingerprint is different from what's stored AND
    // 2. It's not already marked as synced in storage
    if (fingerprint !== storedFingerprint) {
      this.fingerprintSynced = false;
    } else if (
      storedFingerprint &&
      syncedFingerprint &&
      storedFingerprint === syncedFingerprint
    ) {
      // If it matches what's stored and what's synced, make sure to mark it as synced
      this.fingerprintSynced = true;
    }
  }

  /**
   * Get the current fingerprint from memory
   * @returns The current fingerprint or null if not set
   */
  public getFingerprint(): string | null {
    return this.currentFingerprint;
  }

  /**
   * Check if the current fingerprint needs to be synced with the server
   * @returns True if the fingerprint needs to be synced, false otherwise
   */
  public needsSync(): boolean {
    // Force a re-check of the sync status from storage to ensure we have the latest
    this.loadSyncStatus();
    return !this.fingerprintSynced && this.currentFingerprint !== null;
  }
}
